import React, { forwardRef } from 'react';

import type { SvgIconProps } from '@mui/material';
import { SvgIcon } from '@mui/material';

export type { SvgIconProps };
export { SvgIcon };

export enum IconColors {
  Blue = '#0075C4',
  DarkBlue = '#3942B7',
  DarkGrey = '#324A52',
  Error = '#E81486',
  Green = '#00866A',
  Grey = '#D1D1D1',
  LightGray = '#9CA7AB',
  Orange = '#FA715B',
  Primary = '#146EE8',
  Red = '#CB3E3E',
  StarYellow = '#ECD906',
  WarnAmber = '#E87A14',
  WarnYellow = '#E8D314',
  White = '#FFFFFF',
  Yellow = '#F9CB40',
}

/**
 * SVG ids live in the global document namespace, so hardcoded gradient/mask/clip ids can collide
 * when the same icon is mounted in multiple frozen tabs. Generate per-instance ids for reusable icons.
 */
export function useSvgId(prefix: string) {
  return `${prefix}-${React.useId().replace(/:/g, '')}`;
}

// Components using Simple Icons should be able to pass these in
export interface SimpleIconInterface {
  className?: string;
  dataCy?: string;
  fill?: string;
  height?: string | number;
  stroke?: string;
  strokeLinecap?: 'inherit' | 'butt' | 'round' | 'square';
  strokeLinejoin?: 'inherit' | 'round' | 'miter' | 'bevel';
  strokeWidth?: number;
  style?: React.CSSProperties;
  width?: string | number;
}

/**
 * viewBoxDimensions needs to be the `0 0 W H`, where W and H are Width and Height of Zeplin icon.
 * Otherwise scaling will not work.
 * The users of icons SHOULD NOT set the viewBox.
 * Create it once in this file to preserve dimensions. See SendForSignatureIcon for example.
 */
interface SimpleIconProps extends SimpleIconInterface {
  children: React.ReactNode;
  viewBoxDimensions: string;
}

export const SimpleIcon = forwardRef(function SimpleIcon(
  props: SimpleIconProps,
  ref: React.Ref<SVGSVGElement>
): JSX.Element {
  const {
    children,
    className,
    dataCy,
    fill,
    height,
    stroke,
    strokeLinecap,
    strokeLinejoin,
    strokeWidth,
    style,
    viewBoxDimensions,
    width,
  } = props;
  return (
    <svg
      ref={ref}
      className={className}
      data-cy={dataCy}
      fill={fill ?? 'currentColor'}
      height={height ?? 24}
      stroke={stroke}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      strokeWidth={strokeWidth}
      style={style}
      viewBox={viewBoxDimensions}
      width={width ?? 24}
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </svg>
  );
});

interface IconProps extends SvgIconProps {
  path: string;
  fill?: SvgIconProps['fill'];
  fillRule?: SvgIconProps['fillRule'];
}

const Icon = forwardRef(function Icon(
  props: IconProps,
  ref: React.Ref<SVGSVGElement>
): JSX.Element {
  const {
    fill = 'currentColor',
    fillRule = 'nonzero',
    path,
    viewBox = '0 0 24 24',
    ...rest
  } = props;

  return (
    <SvgIcon ref={ref} viewBox={viewBox} {...rest}>
      <path d={path} fill={fill} fillRule={fillRule} />
    </SvgIcon>
  );
});

export type IconPropsSubset = Omit<IconProps, 'path'>;

export function createIcon(props: IconProps) {
  return forwardRef<SVGSVGElement, IconPropsSubset>(function CustomIcon(iconPropsSubset, ref) {
    return <Icon {...props} {...iconPropsSubset} ref={ref} />;
  });
}
